/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */
import { Spinner, SpinnerSize } from "@fluentui/react";
import React, { useState } from "react";
import "./ReferenceHistory.scss";
import { Timeline } from "../Timeline/Timeline";
import { timeLineStyle } from "../Timeline/Timeline.Styles";
import { IStatusText } from "../Timeline/Timeline.types";
import {
  ApplicationStatus,
  ReferenceHistoryTexts,
  TelemetryHistoryLogs,
} from "../constants";
import { applicationApi } from "../../services/application-apis/application-apis";
import moment from "moment";
import { HistoryErrorBody } from "./history-error";
import { ReferenceHistoryHeader } from "./history-header";

interface candidateProps {
  candidateFirstName: string;
  candidateLastName: string;
  status: ApplicationStatus;
}

export const ReferenceHistoryWrapper: React.FC<candidateProps> = ({
  candidateFirstName,
  candidateLastName,
  status,
}) => {
  const [data, setData] = useState([undefined || {}]);
  const [isProgress, setIsProgress] = useState(true);
  const [isErrorOccured, setIsErrorOccured] = useState(false);
  const [isRefresh, setIsRefresh] = useState(true);
  const setIsRefreshTrue = () => setIsRefresh(true);
  const statusText: IStatusText = {
    headingText: ReferenceHistoryTexts.headingText,
    successText: ReferenceHistoryTexts.successText,
    declinedText: ReferenceHistoryTexts.declinedText,
    noResultText: ReferenceHistoryTexts.noResultText,
    downloadText: ReferenceHistoryTexts.downloadText,
  };

  React.useEffect(() => {
    const getHistoryData = () => {
      setIsProgress(true);
      setIsErrorOccured(false);
      applicationApi
        .getReferenceHistory()
        .then((data) => {
          const response = data;
          if (response.status === 200 && response.data.operationResult.result) {
            const sortedData = response.data.operationResult.result;
            //Sorting the history as it's coming as is from services
            sortedData.history &&
              sortedData.history.length &&
              sortedData.history.sort((a: any, b: any) => {
                return moment(new Date(b.statusDate))
                  .local()
                  .diff(moment(new Date(a.statusDate)).local());
              });

            setData(sortedData.history);
            setIsProgress(false);
          }
        })
        .catch((e) => {
          setIsProgress(false);
          setIsErrorOccured(true);
        });
      setIsRefresh(false);
    };

    window.addEventListener(
      TelemetryHistoryLogs.UploadReference,
      getHistoryData
    );
    window.addEventListener(
      TelemetryHistoryLogs.DeclineReference,
      getHistoryData
    );
    window.addEventListener(
      TelemetryHistoryLogs.ServerErrorReference,
      getHistoryData
    );
    window.addEventListener(
      TelemetryHistoryLogs.ReferenceHistoryError,
      getHistoryData
    );

    if (isRefresh === true) {
      getHistoryData(); //This is added to fetch the records for the firstime without any external inputs
    }
    return () => {
      window.removeEventListener(
        TelemetryHistoryLogs.UploadReference,
        getHistoryData
      );
      window.removeEventListener(
        TelemetryHistoryLogs.DeclineReference,
        getHistoryData
      );
      window.removeEventListener(
        TelemetryHistoryLogs.ServerErrorReference,
        getHistoryData
      );
      window.removeEventListener(
        TelemetryHistoryLogs.ReferenceHistoryError,
        getHistoryData
      );
    };
  }, [isRefresh]);
  return (
    <div className="ms-ac-referenceview-app-history-main-container">
      <ReferenceHistoryHeader
        candidateFirstName={candidateFirstName}
        candidateLastName={candidateLastName}
        headingText={statusText.headingText}
      />
      {isProgress === false ? (
        isErrorOccured === true ? (
          <HistoryErrorBody setIsRefreshTrue={setIsRefreshTrue} />
        ) : (
          <>
            <Timeline
              firstName={candidateFirstName}
              lastName={candidateLastName}
              statusText={statusText}
              history={data}
              styles={timeLineStyle}
              status={status}
            />
          </>
        )
      ) : (
        <div className="ms-ac-referenceview-app-history-loader">
          <Spinner
            size={SpinnerSize.large}
            label="Pulling your reference history..."
          />
        </div>
      )}
    </div>
  );
};
